// The browser's half of the session: the platform events and the analytics
// sink it is built with.

import type { SessionAnalytics, SessionLifecycle } from "@aybuben/client/session/session.ts";
import type { PostHog } from "posthog-js";

import { requestPersistence } from "./offline/web.ts";
import { isPostHogConfigured } from "./posthog.ts";

const on = (target: EventTarget, event: string, fn: () => void): (() => void) => {
	target.addEventListener(event, fn);
	return () => target.removeEventListener(event, fn);
};

export const webLifecycle: SessionLifecycle = {
	onOnline: (fn) => on(window, "online", fn),
	onOffline: (fn) => on(window, "offline", fn),
	onForeground: (fn) =>
		on(document, "visibilitychange", () => {
			if (document.visibilityState === "visible") fn();
		}),
	// The engine's own default, spelled out so the session passes it explicitly.
	engineListen: (event, fn) => {
		globalThis.addEventListener?.(event, fn);
		return () => globalThis.removeEventListener?.(event, fn);
	},
	requestPersistence,
};

/** Unconfigured (tests, a build without keys) means no analytics at all. */
export function webAnalytics(posthog: PostHog | undefined): SessionAnalytics | undefined {
	if (!isPostHogConfigured || !posthog) return undefined;
	return {
		capture: (event, ...props) => void posthog.capture(event, props[0]),
		identify: (id, person) => posthog.identify(id, person),
		setPersonProperties: (person) => posthog.setPersonProperties(person),
		reset: () => posthog.reset(),
	};
}
